import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { InputStatistic, TestRunStatistics, TestRunViewModel } from 'src/models/testRun';
import { Property } from 'src/models/property';
import {
	COMPLETE_TEST_RUN_QUERY,
	TEST_RUN_PROPERTIES_QUERY,
	TEST_RUN_STATISTICS_QUERY,
	TEST_RUNS_BY_DATE_QUERY,
	TEST_RUNS_BY_TEST_CASE_UID_QUERY,
	TEST_RUNS_BY_TEST_SPECIFICATION_UID_QUERY,
	TEST_RUNS_BY_TEST_SUITE_UID_QUERY,
} from 'src/graphql/queries/abstraction-layer-queries';
import { LaunchDarklyService } from 'src/services/launchdarkly.service';

import { TestRunBaseService } from './test-run.base.service';
import { ApolloService } from './apollo.service';

@Injectable({ providedIn: 'root' })
export class TestRunGraphQLService extends TestRunBaseService {
	error: any;

	result: Array<TestRunViewModel>;

	constructor(
		private apolloService: ApolloService,
		private launchDarklyService: LaunchDarklyService,
	) {
		super();
	}

	public getTestRunsByDate(startDate: string, endDate: string): Observable<TestRunViewModel[]> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ resultsTestRunModelByDates: TestRunViewModel[] }>({
					query: TEST_RUNS_BY_DATE_QUERY,
					variables: { startDate: startDate, endDate: endDate },
				});
			}),
			map(result => {
				if (result.data && result.data.resultsTestRunModelByDates) {
					return JSON.parse(JSON.stringify(result.data.resultsTestRunModelByDates));
				} else {
					return <TestRunViewModel[]>[];
				}
			}),
			shareReplay(1),
		);
	}

	public getTestRunStatistics(uid: string): Observable<TestRunStatistics> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ resultsStatisticsForTestRun: TestRunStatistics }>({
					query: TEST_RUN_STATISTICS_QUERY,
					variables: { uid: uid },
				});
			}),
			map(result => {
				const statistics = result.data.resultsStatisticsForTestRun;
				const testRunStatistics = new TestRunStatistics();
				testRunStatistics.inputStatistics = statistics.inputStatistics.map((inputStat: any) => {
					const inputStatistic = new InputStatistic();
					inputStatistic.type = inputStat.type;
					inputStatistic.count = inputStat.count;
					return inputStatistic;
				});
				return testRunStatistics;
			}),
			shareReplay(1),
		);
	}

	public getTestRun(uid: string): Observable<TestRunViewModel> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ resultsCompleteTestRun: TestRunViewModel }>({
					query: COMPLETE_TEST_RUN_QUERY,
					variables: { uid: uid },
				});
			}),
			map(result => {
				if (result.data && (result as any).data.testRunModel) {
					return this.normalizeTestRun(JSON.parse(JSON.stringify((result as any).data.testRunModel)));
				} else if (result.data && (result as any).data.resultsCompleteTestRun) {
					return this.normalizeTestRun(JSON.parse(JSON.stringify((result as any).data.resultsCompleteTestRun)));
				} else {
					return new TestRunViewModel();
				}
			}),
			shareReplay(1),
		);
	}

	/**
	 * The API can return null instead of [] for list fields (e.g. a test result with no trouble
	 * reports), so normalize those here rather than relying on every consumer to null-check.
	 * @param testRunViewModel Raw test run view model as parsed from the API response.
	 * @returns The same test run view model with list fields normalized to arrays.
	 */
	private normalizeTestRun(testRunViewModel: TestRunViewModel): TestRunViewModel {
		if (testRunViewModel.testRun) {
			testRunViewModel.testRun.testResults = (testRunViewModel.testRun.testResults ?? []).map(testResult => ({
				...testResult,
				troubleReports: testResult.troubleReports ?? [],
				testCaseAlternativeFailedReasons: testResult.testCaseAlternativeFailedReasons ?? [],
			}));
		}
		return testRunViewModel;
	}

	public getTestRunProperties(uid: string): Observable<Property[]> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ resultsPropertiesForTestRun: Property[] }>({
					query: TEST_RUN_PROPERTIES_QUERY,
					variables: { uid: uid },
				});
			}),
			map(result => {
				if (result.data && result.data.resultsPropertiesForTestRun) {
					return result.data.resultsPropertiesForTestRun;
				} else {
					return <Property[]>[];
				}
			}),
			shareReplay(1),
		);
	}

	public getTestRunsByTestCaseUid(uid: string): Observable<TestRunViewModel[]> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ resultsTestRunModelsByTestCase: TestRunViewModel[] }>({
					query: TEST_RUNS_BY_TEST_CASE_UID_QUERY,
					variables: { uid: uid },
				});
			}),
			map(result => {
				if (result.data && result.data.resultsTestRunModelsByTestCase) {
					return JSON.parse(JSON.stringify(result.data.resultsTestRunModelsByTestCase));
				} else {
					return <TestRunViewModel[]>[];
				}
			}),
			shareReplay(1),
		);
	}

	public getTestRunsByTestSuiteUid(uid: string): Observable<Array<TestRunViewModel>> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ resultsTestRunModelsByTestSuite: TestRunViewModel[] }>({
					query: TEST_RUNS_BY_TEST_SUITE_UID_QUERY,
					variables: { uid: uid },
				});
			}),
			map(result => {
				if (result.data && result.data.resultsTestRunModelsByTestSuite) {
					return JSON.parse(JSON.stringify(result.data.resultsTestRunModelsByTestSuite));
				} else {
					return <TestRunViewModel[]>[];
				}
			}),
			shareReplay(1),
		);
	}

	public getTestRunsByTestSpecificationUid(uid: string): Observable<Array<TestRunViewModel>> {
		return this.apolloService.abstractionLayerClient$.pipe(
			switchMap(client => {
				return client.query<{ resultsTestRunModelsByTestSpecification: TestRunViewModel[] }>({
					query: TEST_RUNS_BY_TEST_SPECIFICATION_UID_QUERY,
					variables: { uid: uid },
				});
			}),
			map(result => {
				if (result.data && result.data.resultsTestRunModelsByTestSpecification) {
					return JSON.parse(JSON.stringify(result.data.resultsTestRunModelsByTestSpecification));
				} else {
					return <TestRunViewModel[]>[];
				}
			}),
			shareReplay(1),
		);
	}
}
